import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Camera, Eye, EyeOff, Clock, Timer } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Loader from '@/components/Loader';
import { trackerService } from '@/services';

// Everything on this page is capture policy the desktop app pulls at launch, so a
// change here reaches every installed client the next time it starts — no rebuild,
// no re-install.
export default function TrackerSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['tracker', 'settings'],
    queryFn: trackerService.getSettings,
  });

  const [form, setForm] = useState(null);
  useEffect(() => {
    if (data?.settings) setForm(data.settings);
  }, [data]);

  const save = useMutation({
    mutationFn: (body) => trackerService.updateSettings(body),
    onSuccess: (res) => {
      setForm(res.settings);
      qc.invalidateQueries({ queryKey: ['tracker'] });
      toast.success('Tracker settings saved. Installed apps pick this up on next break.');
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Could not save'),
  });

  if (isLoading || !form) return <Loader />;
  const hardMax = data?.hardMaxMinutes ?? 15;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = () => {
    save.mutate({
      screenshotMinMinutes: Number(form.screenshotMinMinutes),
      screenshotMaxMinutes: Number(form.screenshotMaxMinutes),
      showScreenshotsToEmployee: form.showScreenshotsToEmployee,
      notifyOnScreenshot: form.notifyOnScreenshot,
      blurScreenshots: form.blurScreenshots,
      captureAllScreens: form.captureAllScreens,
      idleThresholdSeconds: Number(form.idleThresholdSeconds),
      retentionDays: Number(form.retentionDays),
    });
  };

  return (
    <div>
      <PageHeader
        title="Tracker Settings"
        subtitle="Capture policy for the Employee Desk CRM Timer desktop app. Applies to every employee."
        actions={
          <button className="btn-primary" onClick={onSave} disabled={save.isPending}>
            <Save size={16} /> {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        }
      />

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Screenshot interval */}
        <Card>
          <div className="flex items-center gap-2 mb-1">
            <Timer size={18} className="text-brand-600" />
            <h2 className="font-semibold">Screenshot interval</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            A screenshot is taken at a <strong>random</strong> gap between these two, redrawn every
            time — so it lands at 3 min, then 11, then 1. It can never be predicted and timed. Capped
            at {hardMax} minutes whatever you set.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="label">Minimum (minutes)</span>
              <input
                type="number"
                min={1}
                max={hardMax}
                className="input"
                value={form.screenshotMinMinutes}
                onChange={(e) => set('screenshotMinMinutes', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="label">Maximum (minutes)</span>
              <input
                type="number"
                min={1}
                max={hardMax}
                className="input"
                value={form.screenshotMaxMinutes}
                onChange={(e) => set('screenshotMaxMinutes', e.target.value)}
              />
            </label>
          </div>
        </Card>

        {/* Visibility to employees */}
        <Card>
          <div className="flex items-center gap-2 mb-1">
            {form.showScreenshotsToEmployee ? (
              <Eye size={18} className="text-brand-600" />
            ) : (
              <EyeOff size={18} className="text-rose-500" />
            )}
            <h2 className="font-semibold">Employee visibility</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Whether employees can see their own screenshots inside the desktop app. You can always see
            them here regardless.
          </p>
          <Toggle
            label="Show screenshots to employees"
            checked={form.showScreenshotsToEmployee}
            onChange={(v) => set('showScreenshotsToEmployee', v)}
          />
          <Toggle
            label="Notify employee on every capture"
            hint="Strongly recommended — silent capture is what turns a tracker into spyware, and covert monitoring is illegal in many places."
            checked={form.notifyOnScreenshot}
            onChange={(v) => set('notifyOnScreenshot', v)}
          />
          <Toggle
            label="Blur / pixelate screenshots"
            hint="Text becomes unreadable; you can still see roughly what was on screen. Use when staff handle customer data."
            checked={form.blurScreenshots}
            onChange={(v) => set('blurScreenshots', v)}
          />
        </Card>

        {/* Capture */}
        <Card>
          <div className="flex items-center gap-2 mb-1">
            <Camera size={18} className="text-brand-600" />
            <h2 className="font-semibold">Capture</h2>
          </div>
          <div className="mt-3">
            <Toggle
              label="Capture all monitors"
              hint="Off = primary monitor only."
              checked={form.captureAllScreens}
              onChange={(v) => set('captureAllScreens', v)}
            />
          </div>
          <label className="block mt-3">
            <span className="label">Delete screenshots older than (days)</span>
            <input
              type="number"
              min={0}
              className="input"
              value={form.retentionDays}
              onChange={(e) => set('retentionDays', e.target.value)}
            />
            <span className="text-xs text-slate-400">0 = keep forever.</span>
          </label>
        </Card>

        {/* Activity */}
        <Card>
          <div className="flex items-center gap-2 mb-1">
            <Clock size={18} className="text-brand-600" />
            <h2 className="font-semibold">Activity</h2>
          </div>
          <label className="block mt-3">
            <span className="label">Idle after no input for (seconds)</span>
            <input
              type="number"
              min={30}
              className="input"
              value={form.idleThresholdSeconds}
              onChange={(e) => set('idleThresholdSeconds', e.target.value)}
            />
            <span className="text-xs text-slate-400">
              Below this, time still counts as active. 300 = 5 minutes.
            </span>
          </label>
        </Card>
      </div>
    </div>
  );
}

function Toggle({ label, hint, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer">
      <input
        type="checkbox"
        className="mt-1"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}
